import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckboxInput from '../CheckBoxInput'
import styles from './countries-filter-mobile.module.scss'
import { Country, useCountries } from 'hooks/use-countries'
import FilterMenuSection from './FilterMenuSection'
import FilterMenuPage from './FilterMenuPage'
import FilterField from '../FilterField'
import useKeyboardNavigation from 'hooks/use-keyboard-navigation'
import cx from 'classnames'

interface Props {
  filterCountries?: string[]
  setCountries: (countries: string[] | undefined) => void
}

const CountriesFilterMobile: React.FC<Props> = ({ filterCountries, setCountries }) => {
  const { t } = useTranslation('strMobileFilter')
  const { t: tFilterBar } = useTranslation('strFilterBar')
  const [modalOpen, setModalOpen] = useState(false)

  const [inputFilter, setInputFilter] = useState<string>('')
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])

  const handleClear = () => {
    setSelectedCountries([])
    setInputFilter('')
  }

  const handleClose = () => {
    setCountries(selectedCountries.length ? selectedCountries : undefined)
    setInputFilter('')
  }

  const { activeCountriesList, getCountryName } = useCountries()
  const lowerCasedFilter = (inputFilter || '').toLowerCase()
  const filteredCountries =
    activeCountriesList &&
    activeCountriesList.filter(
      (c: Country) => c && c.name.normalize().toLowerCase().startsWith(lowerCasedFilter.normalize())
    )

  const toggleSelection = useCallback(
    (country: Country) => {
      if (selectedCountries.includes(country['alpha-3'])) {
        setSelectedCountries(selectedCountries.filter(el => el !== country['alpha-3']))
      } else {
        setSelectedCountries([...selectedCountries, country['alpha-3']])
      }
    },
    [selectedCountries]
  )

  const { activeIndex, setActiveIndex, isListContainerFocused, listContainerProps } =
    useKeyboardNavigation<HTMLDivElement, Country>({
      listItems: filteredCountries,
      handleSelection: toggleSelection,
    })

  useEffect(() => {
    setSelectedCountries(filterCountries ?? [])
    return () => {
      setSelectedCountries(filterCountries ?? [])
    }
  }, [setSelectedCountries, filterCountries])

  const labelSelection = !selectedCountries.length
    ? t('anyNationality')
    : selectedCountries.length > 1
    ? `${selectedCountries.length}`
    : getCountryName(selectedCountries[0])

  return (
    <>
      <FilterMenuSection
        onClick={() => setModalOpen(true)}
        label={t('nationality')}
        labelSelection={labelSelection}
        classNames={{ filterBtn: styles.filterBtn }}
      />
      <FilterMenuPage
        controls={{ isOpen: modalOpen, setIsOpen: setModalOpen }}
        title={t('nationality')}
        btnRight={{
          content: t('reset'),
          onClick: handleClear,
        }}
        classNames={{ body: styles.contentWrapper }}
        withBackBtn
        onClose={handleClose}
      >
        <FilterField
          value={inputFilter}
          onValueChange={setInputFilter}
          onClear={() => setInputFilter('')}
          inputProps={{
            placeholder: tFilterBar('countryFilterPlaceholder'),
          }}
          classNames={{ input: styles.input }}
        />
        <div
          {...listContainerProps}
          id='countries-list'
          role='listbox'
          className={cx(styles.checkboxGroupContainer, {
            '--is-focused': isListContainerFocused,
          })}
          tabIndex={0}
          aria-activedescendant={filteredCountries[activeIndex]?.name}
        >
          {filteredCountries?.map((country, i) => (
            <CheckboxInput
              key={country['alpha-3']}
              classNames={{
                wrapper: {
                  [styles.checkboxWrapper]: true,
                  '--is-focused': i === activeIndex && isListContainerFocused,
                },
                checkbox: styles.checkbox,
                label: styles.label,
              }}
              onClick={() => {
                toggleSelection(country)
                setActiveIndex(i)
              }}
              label={country.name}
              isChecked={selectedCountries.includes(country['alpha-3'])}
              asOption
              ariaSetsize={filteredCountries.length}
              ariaSelected={i === activeIndex}
            />
          ))}
        </div>
      </FilterMenuPage>
    </>
  )
}

export default CountriesFilterMobile
